
import React, { useState } from 'react';
import { Search, MoreHorizontal, UserPlus, ClipboardPaste, X, ExternalLink, Mail, Building, ShoppingBag, PenTool, Trash2, Check, Save, AlertTriangle, Plus } from 'lucide-react';
import { Client, ClientFilterType } from '../types';
import AddStoreModal from './AddStoreModal';

interface ClientsProps {
    filter: ClientFilterType;
    clients: Client[];
    onSync?: (newClients: Client[]) => void;
    onDelete?: (id: string) => void;
    onUpdate?: (client: Client) => void;
}

const StatusBadge: React.FC<{ status: Client['status'] }> = ({ status }) => {
  const styles = {
    Active: 'bg-emerald-100/50 text-emerald-700 border-emerald-200/50',
    Onboarding: 'bg-blue-100/50 text-blue-700 border-blue-200/50',
    Attention: 'bg-amber-100/50 text-amber-700 border-amber-200/50',
    New: 'bg-slate-100/50 text-slate-700 border-slate-200/50',
  };

  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
      {status}
    </span>
  );
};

const Clients: React.FC<ClientsProps> = ({ filter, clients, onSync, onDelete, onUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [importTab, setImportTab] = useState<'manual' | 'paste'>('manual');
  
  // Paste State
  const [pasteData, setPasteData] = useState('');
  
  // Manual Form State
  const [manualForm, setManualForm] = useState<Partial<Client>>({
      name: '',
      company: '',
      email: '',
      status: 'New',
      subscriptionMonth: 1,
      storeName: '',
      shopifyUrl: ''
  });
  
  // Drawer State
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Client>>({});
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  
  // Delete Confirmation Modal State
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ show: boolean; clientId: string | null; clientName?: string }>({
    show: false,
    clientId: null,
  });

  const getFilteredClients = () => {
      let result = clients;

      switch(filter) {
          case 'insider-service':
              result = clients.filter(c => c.type === 'Lead');
              break;
          case 'onboarding':
              result = clients.filter(c => c.status === 'Onboarding');
              break;
          case 'active':
              result = clients.filter(c => c.type === 'Client' && c.status === 'Active');
              break;
          case 'attention':
              result = clients.filter(c => c.status === 'Attention');
              break;
          case 'm1':
              result = clients.filter(c => c.type === 'Client' && c.subscriptionMonth === 1);
              break;
          case 'm2':
              result = clients.filter(c => c.type === 'Client' && c.subscriptionMonth === 2);
              break;
          case 'm3':
              result = clients.filter(c => c.type === 'Client' && c.subscriptionMonth === 3);
              break;
          case 'm4':
              result = clients.filter(c => c.type === 'Client' && (c.subscriptionMonth || 0) >= 4);
              break;
          case 'finished':
              result = clients.filter(c => c.type === 'Churned' || c.status === 'Active' && (c.subscriptionMonth || 0) >= 12);
              break;
          case 'all':
          default:
              result = clients;
              break;
      }

      return result.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        c.company.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  const filteredClients = getFilteredClients();

  const getTitle = () => {
    switch(filter) {
        case 'active': return "Active GMC's";
        case 'attention': return "Non-active GMC's";
        case 'insider-service': return 'Insider Service';
        case 'm1': return 'Month 1 Upkeep';
        case 'm2': return 'Month 2 Upkeep';
        case 'm3': return 'Month 3 Upkeep';
        case 'm4': return 'Month 4+ Upkeep';
        case 'finished': return 'Finished';
        case 'all': return 'All Clients';
        default: return filter.charAt(0).toUpperCase() + filter.slice(1);
    }
  };

  // ----- Actions -----

  const handlePasteImport = () => {
    if (!pasteData.trim()) return;

    const rows = pasteData.trim().split('\n');
    const newClients: Client[] = [];

    rows.forEach((row, index) => {
        const cells = row.split('\t').map(c => c.trim());
        if (cells.length < 3) return; // Skip invalid rows

        let offset = 0;
        if (cells[0].includes('@')) {
            offset = 0;
        } else if (cells[1] && cells[1].includes('@')) {
            offset = 1;
        }

        const get = (i: number) => cells[i + offset] || '';

        newClients.push({
            id: `manual-${Date.now()}-${index}`,
            email: get(0),
            company: get(1) || 'Unknown Company',
            storeName: get(2),
            address: get(3),
            name: get(4) || 'Unknown Name',
            shopifyUrl: get(5),
            shopifyCode: get(6),
            supportEmail: get(7),
            gmcEmail: get(8),
            companyNumber: get(10),
            vatNumber: get(12),
            type: 'Client',
            mrr: 0, 
            owner: 'Unassigned',
            lastTouch: 'Just now',
            status: 'Active',
            subscriptionMonth: 1
        });
    });

    if (onSync && newClients.length > 0) {
        onSync(newClients);
        setPasteData('');
        setShowImportModal(false);
        setSearchTerm(''); // Clear search so new client shows
    }
  };

  const handleManualSubmit = () => {
      if (!manualForm.name || !manualForm.company) return;

      const newClient: Client = {
          id: `new-${Date.now()}`,
          name: manualForm.name || 'Unknown',
          company: manualForm.company || 'Unknown',
          email: manualForm.email,
          status: manualForm.status as Client['status'] || 'New',
          subscriptionMonth: manualForm.subscriptionMonth || 1,
          storeName: manualForm.storeName,
          shopifyUrl: manualForm.shopifyUrl,
          type: manualForm.status === 'New' || manualForm.status === 'Onboarding' ? 'Lead' : 'Client',
          mrr: 0,
          owner: 'Me',
          lastTouch: 'Just now'
      };

      if (onSync) onSync([newClient]);
      setManualForm({
        name: '', company: '', email: '', status: 'New', subscriptionMonth: 1, storeName: '', shopifyUrl: ''
      });
      setShowImportModal(false);
      setSearchTerm(''); // Clear search so new client shows
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string, clientName?: string) => {
    // 1. Stop row click propagation
    e.stopPropagation();
    
    // 2. Show confirmation modal
    setDeleteConfirmModal({ show: true, clientId: id, clientName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmModal.clientId) return;

    const id = deleteConfirmModal.clientId;

    try {
        // Simulate API Call (e.g., DELETE /api/clients/${id})
        // const response = await fetch(`/api/clients/${id}`, { method: 'DELETE' });
        // if (!response.ok) throw new Error('Failed to delete');
        
        // Simulating network latency
        await new Promise(resolve => setTimeout(resolve, 300));

        // Update Local State on success
        if (onDelete) {
            onDelete(id);
        }

        // Clean up UI (close drawer if the deleted client was selected)
        if (selectedClient?.id === id) {
            setSelectedClient(null);
            setIsEditing(false);
        }

        // Close modal
        setDeleteConfirmModal({ show: false, clientId: null });
    } catch (error) {
        console.error("Failed to delete client:", error);
        setDeleteConfirmModal({ show: false, clientId: null });
        // You could show an error toast here instead of alert
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmModal({ show: false, clientId: null });
  };

  const handleStartEdit = () => {
    if (selectedClient) {
        setEditForm({ ...selectedClient });
        setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSaveEdit = () => {
    if (selectedClient && onUpdate) {
        const updated = { ...selectedClient, ...editForm } as Client;
        onUpdate(updated);
        setSelectedClient(updated);
        setIsEditing(false);
    }
  };

  const handleClientClick = (client: Client) => {
    setSelectedClient(client);
    setIsEditing(false); // Reset edit mode when switching clients
  };

  return (
    <div className="flex flex-col h-full animate-fade-in relative">
      {/* Toolbar */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-slate-800">{getTitle()} <span className="text-slate-400 text-sm ml-2 font-normal">({filteredClients.length})</span></h2>
        
        <div className="flex items-center space-x-3">
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input 
                    type="text" 
                    placeholder="Search clients..." 
                    className="pl-9 pr-4 py-2 bg-white/60 border border-white/40 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-slate-500/20 w-64 transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            
            <button 
                onClick={() => setShowImportModal(true)}
                className="flex items-center space-x-2 bg-slate-900 text-white px-4 py-2 rounded-full hover:bg-slate-800 transition-colors text-sm font-medium shadow-lg shadow-slate-200 cursor-pointer active:scale-95"
            >
                <UserPlus size={16} />
                <span>Add Client</span>
            </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white/90 backdrop-blur-xl border border-white/60 shadow-2xl rounded-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Client</h3>
                  <p className="text-sm text-slate-600">
                    Are you sure you want to delete <span className="font-medium text-slate-900">{deleteConfirmModal.clientName || 'this client'}</span>? This action cannot be undone.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm"
                >
                  Delete Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-white/60 overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white">
                    <h3 className="text-lg font-semibold text-slate-800">Add New Client</h3>
                    <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><X size={20} /></button>
                </div>

                <div className="flex border-b border-slate-100">
                    <button 
                        onClick={() => setImportTab('manual')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${importTab === 'manual' ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-50/30' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        Manual Entry
                    </button>
                    <button 
                        onClick={() => setImportTab('paste')}
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${importTab === 'paste' ? 'text-slate-900 border-b-2 border-slate-900 bg-slate-50/30' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        Paste from Sheet
                    </button>
                </div>
                
                <div className="p-6 overflow-y-auto">
                    {importTab === 'manual' ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Full Name *</label>
                                    <input 
                                        type="text" 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                                        value={manualForm.name}
                                        onChange={e => setManualForm({...manualForm, name: e.target.value})}
                                        placeholder="Jane Doe"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Company Name *</label>
                                    <input 
                                        type="text" 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                                        value={manualForm.company}
                                        onChange={e => setManualForm({...manualForm, company: e.target.value})}
                                        placeholder="Acme Corp"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Email Address</label>
                                    <input 
                                        type="email" 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                                        value={manualForm.email}
                                        onChange={e => setManualForm({...manualForm, email: e.target.value})}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
                                    <select 
                                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:outline-none bg-white"
                                        value={manualForm.status}
                                        onChange={e => setManualForm({...manualForm, status: e.target.value as Client['status']})}
                                    >
                                        <option value="New">New Lead</option>
                                        <option value="Onboarding">Onboarding</option>
                                        <option value="Active">Active Client</option>
                                        <option value="Attention">Needs Attention</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Store Name</label>
                                    <input type="text" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:outline-none" value={manualForm.storeName} onChange={e => setManualForm({...manualForm, storeName: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-slate-700 mb-1">Shopify URL</label>
                                    <input type="text" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:outline-none" value={manualForm.shopifyUrl} onChange={e => setManualForm({...manualForm, shopifyUrl: e.target.value})} />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">Upkeep Month (1-12)</label>
                                <input type="number" min="1" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:outline-none" value={manualForm.subscriptionMonth} onChange={e => setManualForm({...manualForm, subscriptionMonth: parseInt(e.target.value) || 1})} />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-500">
                                Copy rows directly from your Google Sheet or Excel. Format: <br/>
                                <span className="font-mono text-xs text-slate-400">Timestamp | Email | Company | Store | Address | Name ...</span>
                            </p>
                            <textarea 
                                className="w-full h-48 px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none font-mono text-slate-600 resize-none"
                                placeholder="Paste spreadsheet rows here..."
                                value={pasteData}
                                onChange={(e) => setPasteData(e.target.value)}
                            ></textarea>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end space-x-3">
                    <button 
                        onClick={() => setShowImportModal(false)}
                        className="px-4 py-2 text-slate-600 font-medium text-sm hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    {importTab === 'manual' ? (
                        <button 
                            onClick={handleManualSubmit}
                            disabled={!manualForm.name || !manualForm.company}
                            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            <PenTool size={14} className="mr-2" />
                            Create Client
                        </button>
                    ) : (
                        <button 
                            onClick={handlePasteImport}
                            disabled={!pasteData.trim()}
                            className="px-6 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                            <ClipboardPaste size={14} className="mr-2" />
                            Import Rows
                        </button>
                    )}
                </div>
            </div>
        </div>
      )}

      {/* Backdrop for Drawer */}
      {selectedClient && (
        <div 
            className="fixed inset-0 z-30 bg-slate-900/10 backdrop-blur-[2px] transition-opacity"
            onClick={() => setSelectedClient(null)}
        />
      )}

      {/* Client Details Drawer (View & Edit Modes) */}
      <div 
        className={`fixed inset-y-0 right-0 w-96 bg-white/95 backdrop-blur-xl shadow-2xl border-l border-white/50 transform transition-transform duration-300 ease-out z-40 flex flex-col ${selectedClient ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {selectedClient && (
            <>
                <div className="p-6 border-b border-slate-100 flex justify-between items-start">
                    <div>
                        {isEditing ? (
                            <div className="space-y-2">
                                <input 
                                    className="text-xl font-semibold text-slate-800 border-b border-slate-300 focus:border-slate-900 focus:outline-none bg-transparent w-full"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                                />
                                <input 
                                    className="text-sm text-slate-500 border-b border-slate-300 focus:border-slate-900 focus:outline-none bg-transparent w-full"
                                    value={editForm.company}
                                    onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                                />
                            </div>
                        ) : (
                            <>
                                <h2 className="text-xl font-semibold text-slate-800">{selectedClient.name}</h2>
                                <p className="text-sm text-slate-500">{selectedClient.company}</p>
                            </>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setIsStoreModalOpen(true)}
                        className="flex items-center space-x-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors"
                      >
                        <Plus size={12} />
                        <span>Add Store</span>
                      </button>
                      <button onClick={() => setSelectedClient(null)} className="text-slate-400 hover:text-slate-600">
                        <X size={20}/>
                      </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Status Section */}
                    <div className="space-y-3">
                         <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-500">Status</span>
                            {isEditing ? (
                                <select 
                                    className="text-sm border rounded px-2 py-1 bg-white"
                                    value={editForm.status}
                                    onChange={(e) => setEditForm({...editForm, status: e.target.value as Client['status']})}
                                >
                                    <option value="Active">Active</option>
                                    <option value="Onboarding">Onboarding</option>
                                    <option value="Attention">Attention</option>
                                    <option value="New">New</option>
                                </select>
                            ) : (
                                <StatusBadge status={selectedClient.status} />
                            )}
                         </div>
                         <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-500">Upkeep Stage</span>
                            {isEditing ? (
                                <input 
                                    type="number" 
                                    className="text-sm border rounded px-2 py-1 w-16 bg-white"
                                    value={editForm.subscriptionMonth}
                                    onChange={(e) => setEditForm({...editForm, subscriptionMonth: parseInt(e.target.value)})}
                                />
                            ) : (
                                <span className="text-sm text-slate-800 font-medium">Month {selectedClient.subscriptionMonth || 1}</span>
                            )}
                         </div>
                    </div>
                    
                    <hr className="border-slate-100" />

                    {/* Contact Info */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center"><Mail size={14} className="mr-2"/> Contact Details</h3>
                        <div className="space-y-2">
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Email</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.email || ''} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                                ) : (
                                    <p className="text-sm text-slate-700 select-all">{selectedClient.email || '—'}</p>
                                )}
                             </div>
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Support Email</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.supportEmail || ''} onChange={(e) => setEditForm({...editForm, supportEmail: e.target.value})} />
                                ) : (
                                    <p className="text-sm text-slate-700 select-all">{selectedClient.supportEmail || '—'}</p>
                                )}
                             </div>
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">GMC Email</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.gmcEmail || ''} onChange={(e) => setEditForm({...editForm, gmcEmail: e.target.value})} />
                                ) : (
                                    <p className="text-sm text-slate-700 select-all">{selectedClient.gmcEmail || '—'}</p>
                                )}
                             </div>
                        </div>
                    </div>

                    <hr className="border-slate-100" />

                    {/* Business Info */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center"><Building size={14} className="mr-2"/> Business Info</h3>
                        <div className="space-y-2">
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Store Name</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.storeName || ''} onChange={(e) => setEditForm({...editForm, storeName: e.target.value})} />
                                ) : (
                                    <p className="text-sm text-slate-700">{selectedClient.storeName || '—'}</p>
                                )}
                             </div>
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Address</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.address || ''} onChange={(e) => setEditForm({...editForm, address: e.target.value})} />
                                ) : (
                                    <p className="text-sm text-slate-700">{selectedClient.address || '—'}</p>
                                )}
                             </div>
                             <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] uppercase text-slate-400 tracking-wider">Company No</label>
                                    {isEditing ? (
                                        <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.companyNumber || ''} onChange={(e) => setEditForm({...editForm, companyNumber: e.target.value})} />
                                    ) : (
                                        <p className="text-sm text-slate-700 font-mono">{selectedClient.companyNumber || '—'}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase text-slate-400 tracking-wider">VAT Number</label>
                                    {isEditing ? (
                                        <input className="w-full text-sm border-b border-slate-200 focus:border-slate-900 outline-none" value={editForm.vatNumber || ''} onChange={(e) => setEditForm({...editForm, vatNumber: e.target.value})} />
                                    ) : (
                                        <p className="text-sm text-slate-700 font-mono">{selectedClient.vatNumber || '—'}</p>
                                    )}
                                </div>
                             </div>
                        </div>
                    </div>

                    <hr className="border-slate-100" />

                    {/* Shopify Info */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center"><ShoppingBag size={14} className="mr-2"/> Shopify Integration</h3>
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 space-y-3">
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Shop URL</label>
                                {isEditing ? (
                                    <input className="w-full text-sm border-b border-slate-300 focus:border-slate-900 outline-none bg-transparent" value={editForm.shopifyUrl || ''} onChange={(e) => setEditForm({...editForm, shopifyUrl: e.target.value})} />
                                ) : (
                                    <a href={selectedClient.shopifyUrl} target="_blank" rel="noreferrer" className="flex items-center text-sm text-slate-900 hover:underline break-all">
                                        {selectedClient.shopifyUrl || '—'} <ExternalLink size={10} className="ml-1" />
                                    </a>
                                )}
                             </div>
                             <div>
                                <label className="text-[10px] uppercase text-slate-400 tracking-wider">Collaborator Code</label>
                                {isEditing ? (
                                    <input className="w-full text-lg font-mono font-bold text-slate-800 tracking-widest border-b border-slate-300 focus:border-slate-900 outline-none bg-transparent" value={editForm.shopifyCode || ''} onChange={(e) => setEditForm({...editForm, shopifyCode: e.target.value})} />
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <p className="text-lg font-mono font-bold text-slate-800 tracking-widest">{selectedClient.shopifyCode || '—'}</p>
                                    </div>
                                )}
                             </div>
                        </div>
                    </div>

                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-200">
                    {isEditing ? (
                         <div className="flex space-x-2">
                             <button onClick={handleCancelEdit} className="flex-1 py-2 bg-white border border-slate-300 text-slate-600 font-medium rounded-lg text-sm hover:bg-slate-100 transition-colors">
                                Cancel
                             </button>
                             <button onClick={handleSaveEdit} className="flex-1 py-2 bg-slate-900 text-white font-medium rounded-lg text-sm hover:bg-slate-800 transition-colors flex items-center justify-center">
                                <Save size={16} className="mr-2" /> Save Changes
                             </button>
                         </div>
                    ) : (
                        <button onClick={handleStartEdit} className="w-full py-2 bg-white border border-slate-300 text-slate-600 font-medium rounded-lg text-sm hover:bg-slate-100 transition-colors flex items-center justify-center">
                            <PenTool size={14} className="mr-2" /> Edit Details
                        </button>
                    )}
                </div>
            </>
        )}
      </div>

      {/* Add / Edit Store Modal */}
      {selectedClient && (
        <AddStoreModal
          clientName={selectedClient.name}
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
          onSave={(values) => {
            // For now, just log the values; persistence will be wired via API/DB later.
            console.log('Store saved for client', selectedClient.id, values);
          }}
        />
      )}

      {/* Table */}
      <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-white/40 shadow-sm overflow-hidden flex-1">
        {filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <p>No clients found for this view.</p>
            </div>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[800px]">
                    <thead>
                        <tr className="border-b border-slate-200/50 text-slate-500 text-xs font-medium uppercase tracking-wider">
                            <th className="p-4 font-normal pl-6">Client</th>
                            <th className="p-4 font-normal">Status</th>
                            <th className="p-4 font-normal">Month</th>
                            <th className="p-4 font-normal">Email</th>
                            <th className="p-4 font-normal">Shopify</th>
                            <th className="p-4 font-normal">Last Touch</th>
                            <th className="p-4 font-normal w-10"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClients.map((client) => (
                            <tr 
                                key={client.id} 
                                onClick={() => handleClientClick(client)}
                                className="group hover:bg-white/50 transition-colors border-b border-slate-100/50 last:border-0 cursor-pointer"
                            >
                                <td className="p-4 pl-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-semibold text-slate-600">
                                            {client.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-slate-800">{client.name}</div>
                                            <div className="text-xs text-slate-500">{client.company}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <StatusBadge status={client.status} />
                                </td>
                                <td className="p-4 text-sm text-slate-500">
                                    {client.subscriptionMonth ? `M${client.subscriptionMonth}` : '-'}
                                </td>
                                <td className="p-4 text-sm text-slate-500 truncate max-w-[150px]">
                                    {client.email || '-'}
                                </td>
                                <td className="p-4 text-sm text-slate-500">
                                    {client.shopifyCode ? <span className="font-mono bg-slate-100 px-1 rounded text-xs">{client.shopifyCode}</span> : '-'}
                                </td>
                                <td className="p-4 text-sm text-slate-500">
                                    {client.lastTouch}
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex items-center justify-end space-x-2">
                                        <button 
                                            onClick={(e) => handleDeleteClick(e, client.id, client.name)}
                                            className="text-slate-300 hover:text-red-500 transition-colors p-2 rounded-full hover:bg-red-50 z-10"
                                            title="Delete Client"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
      </div>
    </div>
  );
};

export default Clients;
